"use client"

/**
 * Shared UI primitives for the ad-math calculator tools under /tools.
 *
 * Each calculator widget composes these: a `CalculatorCard` shell, one or more
 * `NumberField` inputs (with $ / % adornments), `ResultStat` outputs, and a
 * `FormulaNote` caption that states the formula and unit conventions. Keeping the
 * chrome here means every calculator looks consistent and only ships its formula.
 */

import type { ReactNode } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"

export function CalculatorCard({
  title,
  children,
}: {
  title?: string
  children: ReactNode
}) {
  return (
    <Card className="border-border bg-card shadow-sm">
      <CardContent className="p-6 sm:p-8">
        {title ? (
          <h2 className="mb-6 text-lg font-semibold tracking-tight text-foreground">
            {title}
          </h2>
        ) : null}
        {children}
      </CardContent>
    </Card>
  )
}

export function CalculatorGrid({ children }: { children: ReactNode }) {
  return <div className="grid gap-5 sm:grid-cols-2">{children}</div>
}

/**
 * A labeled numeric input. `value` is held as a string by the parent so the
 * field can be cleared without snapping to 0; widgets parse it for the maths.
 * `prefix` ($) and `suffix` (%) are purely visual adornments.
 */
export function NumberField({
  id,
  label,
  value,
  onChange,
  prefix,
  suffix,
  min = 0,
  step = "any",
  placeholder,
  hint,
}: {
  id: string
  label: string
  value: string
  onChange: (next: string) => void
  prefix?: string
  suffix?: string
  min?: number
  step?: string | number
  placeholder?: string
  hint?: string
}) {
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id} className="text-sm font-medium text-foreground">
        {label}
      </Label>
      <div className="relative">
        {prefix ? (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
            {prefix}
          </span>
        ) : null}
        <Input
          id={id}
          type="number"
          inputMode="decimal"
          min={min}
          step={step}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={cn(prefix && "pl-7", suffix && "pr-9")}
        />
        {suffix ? (
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
            {suffix}
          </span>
        ) : null}
      </div>
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  )
}

/**
 * A highlighted result readout. `tone` tints the value for pass/warn/neutral
 * verdicts (e.g. an LTV:CAC ratio below 1 renders in red).
 */
export function ResultStat({
  label,
  value,
  caption,
  tone = "neutral",
}: {
  label: string
  value: string
  caption?: string
  tone?: "neutral" | "good" | "warn" | "bad"
}) {
  const toneClass = {
    neutral: "text-foreground",
    good: "text-emerald-600 dark:text-emerald-400",
    warn: "text-amber-600 dark:text-amber-400",
    bad: "text-red-600 dark:text-red-400",
  }[tone]

  return (
    <div className="rounded-2xl border border-border bg-muted/40 p-6">
      <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <div className={cn("mt-1 font-mono text-4xl font-bold tracking-tight", toneClass)}>
        {value}
      </div>
      {caption ? (
        <p className="mt-2 text-sm text-muted-foreground">{caption}</p>
      ) : null}
    </div>
  )
}

export function FormulaNote({ children }: { children: ReactNode }) {
  return (
    <p className="mt-6 rounded-lg border border-dashed border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
      {children}
    </p>
  )
}

/** Format a number as a currency-ish string with thousands separators. */
export function formatCurrency(n: number, currency = "$"): string {
  if (!Number.isFinite(n)) return "—"
  return `${currency}${n.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`
}

/** Format a multiple like ROAS — e.g. 2.5 → "2.5×". */
export function formatMultiple(n: number): string {
  if (!Number.isFinite(n)) return "—"
  return `${n.toLocaleString("en-US", { maximumFractionDigits: 2 })}×`
}

/** Format a 0–1 fraction as a percentage — e.g. 0.4 → "40%". */
export function formatPercent(fraction: number): string {
  if (!Number.isFinite(fraction)) return "—"
  return `${(fraction * 100).toLocaleString("en-US", { maximumFractionDigits: 1 })}%`
}

/** Parse a held input string to a finite number, or `null` when empty/invalid. */
export function parseNum(raw: string): number | null {
  if (raw.trim() === "") return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}
